import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import MotionWrapper from '@atoms/MotionWrapper/MotionWrapper';
import type { IImage } from '@models/IImage';
import type { ILink } from '@models/ILink';
import type { IMeta } from '@models/IMeta';
import type { IProductCard } from '@models/IProductCard';
import type { IUnifiedProductCard } from '@models/IUnifiedProductCard';
import type { IVideo } from '@models/IVideo';
import GridProductCard from '@molecules/GridProductCard/GridProductCard';
import InViewVideo from '@molecules/InViewVideo/InViewVideo';
import MediaGridCarousel from '@molecules/MediaGridCarousel/MediaGridCarousel';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import UnifiedProductCard from '@molecules/UnifiedProductCard/UnifiedProductCard';
import updateDataLayer from '@utils/helpers/gtm';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';

interface Props {
  readonly title: string;
  readonly text?: string;
  readonly cta?: ILink;
  readonly mainProduct: IProductCard;
  readonly products: IUnifiedProductCard[];
  readonly portraitMedia: IImage | IVideo;
  readonly landscapeMedia: IImage | IVideo;
  readonly mediaPills?: string[];
  readonly anchorId?: string;
  readonly meta?: IMeta;
}

const ProductsWithMediaGrid = ({
  title,
  text,
  cta,
  mainProduct,
  products,
  portraitMedia,
  landscapeMedia,
  mediaPills,
  anchorId,
  meta,
}: Props) => {
  const isMobileViewport = useMediaQuery(1440);
  const isCompact = products.length === 3;

  return (
    <section
      className="component-padding bg-white text-black-100 dark:bg-black-100 dark:text-white"
      data-testid="CT-173-Products-With-Media-Grid"
      id={`a-${anchorId ?? 'products-with-media-grid'}`}
    >
      <SoftLoadWrapper>
        <div className="container">
          <h2
            data-testid="CT-173-Products-With-Media-Grid-Title"
            data-epi-property-name={meta?.editHints?.title}
          >
            {title}
          </h2>

          {(text || cta) && (
            <div className="mt-10 flex gap-10 max-lg:flex-col lg:items-center lg:justify-between">
              {text && (
                <div
                  className="body-one lg:w-3/5 xl:w-1/2"
                  dangerouslySetInnerHTML={{ __html: text }}
                  data-testid="CT-173-Products-With-Media-Grid-Subtitle"
                  data-epi-property-name={meta?.editHints?.text}
                />
              )}

              {cta?.url && cta.name && (
                <div
                  className="shrink-0"
                  data-epi-property-name={meta?.editHints?.cta}
                >
                  <Button
                    link={cta}
                    linkCallback={() => {
                      updateDataLayer({
                        event: 'selectContent',
                        content_name: title,
                        link_text: cta.name,
                      });
                    }}
                    dataTestId="CT-173-Products-With-Media-Grid-CTA"
                  />
                </div>
              )}
            </div>
          )}

          <MotionWrapper
            className="mt-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: isMobileViewport === undefined ? 0 : 1 }}
          >
            {isMobileViewport ? (
              <div className="flex flex-col gap-y-10">
                <div
                  className="h-[500px] w-full md:hidden"
                  data-epi-property-name={meta?.editHints?.portraitMedia}
                >
                  {portraitMedia &&
                  'url' in portraitMedia &&
                  portraitMedia.url ? (
                    <InViewVideo
                      video={portraitMedia as IVideo}
                      videoClasses="[&>div>video]:object-cover h-full pointer-events-none"
                      wrapperClasses="w-full h-full"
                      autoPlay
                      hasInVideoPlayButton
                      controls={false}
                      dataTestId="CT-173-Products-With-Media-Grid-Video-Card-Portrait"
                    />
                  ) : (
                    <Image
                      image={portraitMedia as IImage}
                      classes="h-full w-full"
                      imageClasses="w-full h-full object-cover"
                      dataTestId="CT-173-Products-With-Media-Grid-Image-Card-Portrait"
                    />
                  )}
                </div>

                <div
                  className="w-full max-md:hidden"
                  data-epi-property-name={meta?.editHints?.landscapeMedia}
                >
                  {landscapeMedia &&
                  'url' in landscapeMedia &&
                  landscapeMedia.url ? (
                    <InViewVideo
                      video={landscapeMedia as IVideo}
                      videoClasses="[&>div>video]:object-cover h-full pointer-events-none max-h-[80vh]"
                      wrapperClasses="w-full h-full"
                      autoPlay
                      hasInVideoPlayButton
                      controls={false}
                      dataTestId="CT-173-Products-With-Media-Grid-Video-Card-Landscape"
                    />
                  ) : (
                    <Image
                      image={landscapeMedia as IImage}
                      classes="h-full w-full"
                      imageClasses="w-full h-full object-cover max-h-[80vh]"
                      dataTestId="CT-173-Products-With-Media-Grid-Image-Card-Landscape"
                    />
                  )}
                </div>

                <MediaGridCarousel
                  carouselId="Products-With-Media-Grid"
                  carouselNavId="products-with-media-grid-carousel"
                  dataTestId="CT-173-Products-With-Media-Grid"
                  meta={meta}
                  products={[mainProduct, ...products]}
                  title={title}
                />
              </div>
            ) : (
              <div
                className={classNames(
                  'grid grid-cols-12',
                  isCompact
                    ? 'grid-rows-3 gap-x-4 gap-y-5'
                    : 'grid-rows-2 gap-4'
                )}
              >
                <div className="col-span-5 row-span-full">
                  <GridProductCard
                    {...mainProduct}
                    isLargeCard
                    largeCardProductImageGap="gap-y-5 lg:gap-y-13 xl:gap-y-16"
                    largeCardPadding="px-5 py-5 lg:py-8 xl:pt-14 xl:pb-10"
                    productNameClassname="h5"
                    showCategory
                    dataTestId="CT-173-Products-With-Media-Grid-Main-Product-Card"
                    data-epi-property-name={meta?.editHints?.mainProduct}
                  />
                </div>

                {products.map((product, index) => (
                  <div
                    className="col-span-4"
                    key={product.name}
                    data-epi-property-name={meta?.editHints?.products}
                  >
                    <UnifiedProductCard
                      {...product}
                      isCompact={isCompact}
                      trackingDataPosition={index + 1}
                      dataTestId={`CT-173-Products-With-Media-Grid-Product-Card-${index}`}
                    />
                  </div>
                ))}

                <div
                  className="relative col-span-3 col-start-10 row-span-full"
                  data-epi-property-name={meta?.editHints?.portraitMedia}
                >
                  {portraitMedia &&
                  'url' in portraitMedia &&
                  portraitMedia.url ? (
                    <InViewVideo
                      video={portraitMedia as IVideo}
                      videoClasses="[&>div>video]:object-cover h-full pointer-events-none"
                      wrapperClasses="w-full h-full"
                      autoPlay
                      hasInVideoPlayButton
                      controls={false}
                      dataTestId="CT-173-Products-With-Media-Grid-Video-Card"
                    />
                  ) : (
                    <Image
                      image={portraitMedia as IImage}
                      classes="h-full w-full"
                      imageClasses="w-full h-full object-cover"
                      dataTestId="CT-173-Products-With-Media-Grid-Image-Card"
                    />
                  )}

                  {!!mediaPills?.length && (
                    <div
                      data-testid="CT-173-Products-With-Media-Grid-Pills"
                      className="absolute top-[18px] right-[18px] left-[18px] flex flex-wrap gap-2.5 max-xl:hidden"
                      data-epi-property-name={meta?.editHints?.mediaPills}
                    >
                      {mediaPills.map((pill) => (
                        <p
                          key={pill}
                          className="body-two rounded-full bg-grey-600 px-4 py-1 text-white"
                        >
                          {pill}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </MotionWrapper>
        </div>
      </SoftLoadWrapper>
    </section>
  );
};

export default ProductsWithMediaGrid;
